import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Viewer } from "@creators/backend/lib/auth";
import { clientApi } from "@frontend/lib/api";
import { cacheClear } from "@frontend/lib/data-cache";

type ViewerContextValue = {
  viewer: Viewer | null;
  /** True only until the first /api/viewer response — not on later refreshes. */
  loading: boolean;
  reload: () => Promise<void>;
};

const ViewerContext = createContext<ViewerContextValue | null>(null);

export async function fetchViewer(): Promise<Viewer | null> {
  const { viewer } = await clientApi<{ viewer: Viewer | null }>("/api/viewer");
  return viewer;
}

export function ViewerProvider({ children }: { children: ReactNode }) {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [booted, setBooted] = useState(false);

  const reload = useCallback(async () => {
    try {
      setViewer(await fetchViewer());
    } catch {
      setViewer(null);
    } finally {
      setBooted(true);
    }
  }, []);

  // Boot once. Live `refresh()` must not re-hit /api/viewer — that storms the
  // mobile network whenever a collab poll bumps generation.
  useEffect(() => {
    void reload();
  }, [reload]);

  const prevSessionId = useRef<string | null>(null);
  useEffect(() => {
    const sessionId = viewer?.sessionId ?? null;
    if (prevSessionId.current !== null && prevSessionId.current !== sessionId) {
      cacheClear();
    }
    prevSessionId.current = sessionId;
  }, [viewer?.sessionId]);

  const value = useMemo(
    () => ({ viewer, loading: !booted, reload }),
    [viewer, booted, reload],
  );
  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
}

export function useViewer(): ViewerContextValue {
  const ctx = useContext(ViewerContext);
  if (!ctx) throw new Error("useViewer must be used within ViewerProvider");
  return ctx;
}
